// dealScore.ts — Phase-1 transparent heuristic Deal Score (closes #158).
//
// The 2026-04-28 UX audit asked for a single 0-100 composite score
// replacing the unexplained dual-bar visualization on cards. This is
// the heuristic Phase 1 of #158: explicitly *not* an ML model, just
// transparent weighted factors investors can sanity-check.
//
// ## Formula
//
//   score = clamp(0..100,
//       40 * normalize(equity_pct, 0..50%)         // headline factor
//     + 25 * actionability(status)                 // can you act on it?
//     + 20 * urgency(days_to_auction)              // does it matter now?
//     + 15 * sanity(assessedValue + lotSize ranges) // garbage data penalty
//   )
//
// Bands:
//   80+    → "Strong deal" / green
//   60-79  → "Worth a look" / amber
//   40-59  → "Marginal" / muted gray
//   <40    → "Skip" / muted gray
//
// ## What's explicitly NOT in scope
//
// - Property comps / ARV (would require external data partnership)
// - School / crime / neighborhood signals (same)
// - Title / lien encumbrances (would require ROD scrape; #161)
// - Owner contact / motivation (would require skip-tracing partnership)
//
// Phase 2 of #158 may layer those in. Phase 1 is what's computable
// purely from the existing Property record.

import { Property } from "../types";
import { statusMeta } from "./statusMeta";
import { formatCurrencyShort } from "./format";
import { resolveAmountOwed } from "./dataQuality";

export type ScoreBand = "strong" | "worth-look" | "marginal" | "skip";

export interface DealScoreFactor {
  /** Human-readable factor name shown in the breakdown UI. */
  label: string;
  /** 0..1 — this factor's contribution before its weight is applied. */
  raw: number;
  /** 0..100 — how much this factor adds to the final score (raw × weight). */
  weighted: number;
  /** Max points this factor could contribute (the weight). Used to render "x / 25". */
  weight: number;
  /** Short human-readable explanation shown on hover / in the breakdown. */
  reason: string;
}

export interface DealScore {
  /** Final score, integer 0..100. */
  score: number;
  /** Band for color/label rendering. */
  band: ScoreBand;
  /** Per-factor breakdown so the score is auditable / educational. */
  factors: DealScoreFactor[];
}

const WEIGHT_EQUITY = 40;
const WEIGHT_ACTIONABILITY = 25;
const WEIGHT_URGENCY = 20;
const WEIGHT_SANITY = 15;

function clamp01(n: number): number {
  if (!isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function bandFor(score: number): ScoreBand {
  if (score >= 80) return "strong";
  if (score >= 60) return "worth-look";
  if (score >= 40) return "marginal";
  return "skip";
}

export function bandLabel(band: ScoreBand): string {
  switch (band) {
    case "strong": return "Strong deal";
    case "worth-look": return "Worth a look";
    case "marginal": return "Marginal";
    case "skip": return "Skip";
  }
}

/** Hex color for each band, aligned with index.css :root tokens. */
export function bandColor(band: ScoreBand): string {
  switch (band) {
    case "strong": return "#16a34a";    // --accent-green
    case "worth-look": return "#d97706"; // --accent-amber
    case "marginal": return "#94a3b8";  // muted gray
    case "skip": return "#64748b";      // --text-muted
  }
}

// ── Per-factor scoring ──────────────────────────────────────────────

// Haircut on the equity factor when the debt is the deed-of-trust ORIGINAL
// principal rather than a published figure. That number misses in both
// directions: an older loan has been paid down (debt overstated, equity
// understated), while a loan in default carries arrears, interest and attorney
// fees on top (debt understated, equity overstated). Only the second direction
// can mislead a bidder into overpaying, and it is usually the smaller error
// (months of arrears against years of amortisation), so the cut is modest:
// 0.85 × 40 = at most 34 points instead of 40. Mirrored by
// derivedPrincipalHaircut in backend/internal/leadscore/score.go.
//
// Opening bids (published_bid / opening_bid) are NOT cut: a tax opening bid IS
// the debt, a firm's opening bid is the attorney's own number, and promoted bids
// have always scored at full weight, so cutting them would move scores that were
// never wrong.
export const ORIGINAL_PRINCIPAL_EQUITY_HAIRCUT = 0.85;

// Short provenance suffix for the equity reason. The detail panel carries the
// full caveat (resolveAmountOwed's note); this only has to say where the debt
// figure came from so the score and the panel are visibly the same number.
const DEBT_SOURCE_SUFFIX: Record<string, string> = {
  published_bid: " · debt from published bid",
  opening_bid: " · debt from opening bid",
  original_principal:
    " · est. from original loan amount: may be paid down, or higher with fees",
};

/**
 * Equity factor: assessed - amount owed, normalized as a percentage of
 * assessedValue, capped at 50% (anything above is essentially "free").
 * Gives full 1.0 at ≥50% equity, 0.0 at ≤0% equity (underwater).
 *
 * The amount owed is resolveAmountOwed's figure, the same one the detail
 * panel shows, so the score can never say "debt unknown" next to a panel
 * showing a dollar amount (2026-10-22: 512 live properties did exactly that,
 * because this read only p.amountDue while the panel fell back to the opening
 * bid and the original principal).
 */
function equityRaw(p: Property): { raw: number; reason: string } {
  if (!p.assessedValue || p.assessedValue <= 0) {
    return { raw: 0, reason: "No assessed value — cannot compute equity" };
  }
  // amountDue === 0 is a "this source has no amount-due concept" sentinel, not
  // a genuine zero balance (#1062); resolveAmountOwed already treats it as
  // absent and falls back to the bid / original principal, or returns
  // "unavailable". Treating 0 as zero debt would inflate equity to the FULL
  // assessed value, which makes an unknown deal look free-and-clear.
  const owed = resolveAmountOwed(p);
  if (owed.state === "unavailable" || !owed.amount || owed.amount <= 0) {
    return { raw: 0, reason: "Debt amount not published — equity unknown" };
  }
  const suffix = DEBT_SOURCE_SUFFIX[owed.source ?? ""] ?? "";
  const equity = p.assessedValue - owed.amount;
  if (equity <= 0) {
    return { raw: 0, reason: `Underwater: ${Math.round((equity / p.assessedValue) * 100)}% equity${suffix}` };
  }
  const pct = equity / p.assessedValue;
  let raw = clamp01(pct / 0.5); // 50% equity → full 1.0
  if (owed.source === "original_principal") {
    raw *= ORIGINAL_PRINCIPAL_EQUITY_HAIRCUT;
  }
  return {
    raw,
    reason: `${Math.round(pct * 100)}% equity (${formatShort(equity)} of ${formatShort(p.assessedValue)})${suffix}`,
  };
}

/**
 * Actionability factor: how soon can a bidder act on this property?
 *   auction_scheduled → 1.0  (you can show up at the courthouse)
 *   notice_filed     → 0.6  (sale date will arrive within weeks)
 *   notice_of_default → 0.3 (months out)
 *   in_rem / res_plus → 0.5 (active proceedings, mid-pipeline)
 *   terminal states  → 0.0 (sold/redeemed/withdrawn — too late)
 */
function actionabilityRaw(p: Property): { raw: number; reason: string } {
  const meta = statusMeta(p.status);
  if (meta.isTerminal) {
    return { raw: 0, reason: `${meta.label} — case closed, no longer biddable` };
  }
  // The two live auction states the stage ladder does not express on its own:
  // a held sale awaiting confirmation (an upset bid can still reopen it) and a
  // sale whose date moved.
  if (p.status === "pending_confirmation") {
    return { raw: 0.8, reason: `${meta.label} — sale held; an upset bid can still reopen it` };
  }
  if (p.status === "postponed") {
    return { raw: 0.6, reason: `${meta.label} — sale moved; new date pending` };
  }
  switch (meta.lifecycleStage) {
    case 0: return { raw: 0.3, reason: `${meta.label} — months from auction` };
    case 1: return { raw: 0.6, reason: `${meta.label} — sale date pending` };
    case 2: return { raw: 1.0, reason: `${meta.label} — actively biddable` };
    default: return { raw: 0, reason: meta.label };
  }
}

/**
 * Urgency factor: days remaining until the auction. Higher score for
 * closer-in deals — for the audit's stated goal ("Auction in 3 days"
 * urgency should propel the score), the curve has to fall off
 * meaningfully past 30 days.
 *
 *   <7 days   → 1.0   (max — drop everything and bid)
 *   <14 days  → 0.8
 *   <30 days  → 0.55
 *   <60 days  → 0.3   (lukewarm)
 *   <90 days  → 0.15
 *   ≥90 days  → 0.05
 *   no date   → 0.0
 */
function urgencyRaw(p: Property): { raw: number; reason: string } {
  if (!p.auctionDate) {
    return { raw: 0, reason: "No auction date set yet" };
  }
  const target = new Date(p.auctionDate).getTime();
  if (isNaN(target)) {
    return { raw: 0, reason: "Auction date unparseable" };
  }
  const days = Math.floor((target - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0) {
    return { raw: 0, reason: `Auction was ${-days} days ago — outcome pending` };
  }
  if (days < 7) return { raw: 1.0, reason: `Auction in ${days} day${days === 1 ? "" : "s"}` };
  if (days < 14) return { raw: 0.8, reason: `Auction in ${days} days` };
  if (days < 30) return { raw: 0.55, reason: `Auction in ${days} days` };
  if (days < 60) return { raw: 0.3, reason: `Auction in ${days} days` };
  if (days < 90) return { raw: 0.15, reason: `Auction in ${days} days` };
  return { raw: 0.05, reason: `Auction ${days}+ days out` };
}

// Property classes for which the score's underlying data (assessed
// value, comps, residential-flavored lot-size sanity) is well-grounded.
// Anything else hits the non-residential confidence cap below — we
// have no paid market data for cap-rate / NOI / per-acre comps yet, so
// the score is less reliable for those classes (#243 sub-PR e). Phase
// 2 will replace this cap with real per-class benchmarks.
const RESIDENTIAL_CLASSES = new Set(["residential", "", "unknown"]);

/**
 * Returns true when the property's class is non-residential AND we
 * therefore have no paid market data backing the score. Used to cap
 * the Data Sanity factor (#243 Phase 1).
 */
function isNonResidentialClass(p: Property): boolean {
  const cls = (p.propertyClass || "").toLowerCase().trim();
  return cls !== "" && !RESIDENTIAL_CLASSES.has(cls);
}

// Cap applied to Data Sanity for non-residential properties — we have
// class-appropriate UI but no real-time comps yet, so the score is
// less confident. 0.6 × 15 = ~9 points (vs. ~15 for residential), so
// non-residential scores top out ~6 points lower than equivalent
// residential ones. Phase 2 (paid market data) restores parity.
const NON_RESIDENTIAL_SANITY_CAP = 0.6;

/**
 * Sanity factor: penalize properties with garbage data — assessed value
 * absurdly low, lot size in nonsense range, etc. Returns 1.0 for "data
 * looks plausible," lower for suspect entries.
 *
 * The Polaris lot-size unit-confusion bug (#69) showed why this
 * matters: a 500-sqft parcel mis-stored as 500 acres would otherwise
 * score perfectly on equity and crater your day. This factor protects
 * the user from acting on bad data.
 *
 * Phase-1 (#243 sub-PR e) addition: non-residential property classes
 * cap at NON_RESIDENTIAL_SANITY_CAP (0.6) because we have no paid
 * market data backing the score for commercial / multi-family /
 * industrial / land. This signals to users that the score is less
 * confident for those classes; the per-class analyzer UI is still
 * fully functional.
 */
function sanityRaw(p: Property): { raw: number; reason: string } {
  const reasons: string[] = [];
  let raw = 1.0;
  if (!p.assessedValue || p.assessedValue < 5000) {
    raw = 0;
    reasons.push("assessed value < $5K");
  } else if (p.assessedValue < 30000) {
    raw = Math.min(raw, 0.5);
    reasons.push("assessed value < $30K");
  }
  // Lot size sanity — Polaris bug #69 historical context. We only need
  // the acres reading (not the sqft) to detect the unit-confusion case;
  // a residential parcel with > 100 acres is overwhelmingly the bug.
  // For LAND properties (where many-acre lots are perfectly normal) we
  // skip this check entirely.
  const isLand = (p.propertyClass || "").toLowerCase() === "land_only" ||
                 (p.propertyClass || "").toLowerCase() === "agricultural";
  if (!isLand) {
    // Server-parsed (#1872): arrives as a number or not at all, so the
    // string-or-number dance this used to do is gone.
    const acresNum = p.building?.lotSizeAcres;
    if (acresNum !== undefined && acresNum > 100) {
      raw = Math.min(raw, 0.3);
      reasons.push(`${acresNum.toFixed(0)}-acre lot (likely Polaris unit bug, see #69)`);
    }
  }
  // Non-residential confidence cap — apply LAST so the explicit
  // garbage-data penalties above are preserved when stricter than
  // the cap.
  if (isNonResidentialClass(p) && raw > NON_RESIDENTIAL_SANITY_CAP) {
    raw = NON_RESIDENTIAL_SANITY_CAP;
    reasons.push("non-residential class — no market-data benchmarks yet");
  }
  if (reasons.length === 0) {
    return { raw: 1.0, reason: "Data ranges look plausible" };
  }
  return { raw, reason: reasons.join(" · ") };
}

// Delegates to lib/format.ts (#1033) — matches this function's prior
// behavior exactly (1-decimal millions, "$0" fallback).
function formatShort(value: number): string {
  return formatCurrencyShort(value);
}

// Hard cap on terminal-status scores. Once a property is sold/redeemed/
// withdrawn/etc., it's not biddable — the user's eye should NEVER see a
// terminal property labeled anything but "Skip" regardless of how strong
// the equity / data factors look. Cap below the marginal threshold (40)
// so band is always "skip" for terminal cases.
const TERMINAL_SCORE_CAP = 30;

/**
 * Compute the deal score for a property. Pure function — no I/O, no
 * Date side effects beyond reading Date.now() for urgency. Stable on
 * a given property + clock.
 */
export function computeDealScore(p: Property): DealScore {
  const equity = equityRaw(p);
  const action = actionabilityRaw(p);
  const urgency = urgencyRaw(p);
  const sanity = sanityRaw(p);

  const factors: DealScoreFactor[] = [
    {
      label: "Equity",
      raw: equity.raw,
      weight: WEIGHT_EQUITY,
      weighted: WEIGHT_EQUITY * equity.raw,
      reason: equity.reason,
    },
    {
      label: "Actionability",
      raw: action.raw,
      weight: WEIGHT_ACTIONABILITY,
      weighted: WEIGHT_ACTIONABILITY * action.raw,
      reason: action.reason,
    },
    {
      label: "Urgency",
      raw: urgency.raw,
      weight: WEIGHT_URGENCY,
      weighted: WEIGHT_URGENCY * urgency.raw,
      reason: urgency.reason,
    },
    {
      label: "Data sanity",
      raw: sanity.raw,
      weight: WEIGHT_SANITY,
      weighted: WEIGHT_SANITY * sanity.raw,
      reason: sanity.reason,
    },
  ];

  let score = Math.round(factors.reduce((sum, f) => sum + f.weighted, 0));
  let clamped = Math.max(0, Math.min(100, score));

  // Apply terminal cap AFTER computing factors so the breakdown UI
  // still shows the user WHAT a hypothetical strong equity would have
  // contributed — but the headline number reflects "this isn't biddable
  // anymore." Equity isn't fake, but acting on it is impossible.
  if (statusMeta(p.status).isTerminal && clamped > TERMINAL_SCORE_CAP) {
    clamped = TERMINAL_SCORE_CAP;
  }

  return {
    score: clamped,
    band: bandFor(clamped),
    factors,
  };
}
